import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, Building2, Wrench, Ticket, FileText, Receipt,
  Package, Briefcase, Flame, Search, ArrowRight, Loader2,
  ScrollText, AlertTriangle, History,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/context/AuthContext";
import VoiceInput from "@/components/VoiceInput";
import { BACKEND_URL } from "@/lib/api";

const API = BACKEND_URL;

// Module → icoon + label mapping
export const MODULE_META = {
  vaste_klant: { label: "Vaste klanten", icon: Building2, color: "text-blue-600" },
  medewerker: { label: "Medewerkers", icon: Users, color: "text-emerald-600" },
  werkbon: { label: "Werkbonnen", icon: Wrench, color: "text-orange-600" },
  ticket: { label: "Tickets", icon: Ticket, color: "text-red-600" },
  factuur: { label: "Facturen", icon: Receipt, color: "text-purple-600" },
  offerte: { label: "Offertes", icon: FileText, color: "text-indigo-600" },
  asset: { label: "Assets", icon: Package, color: "text-yellow-600" },
  bb_project: { label: "BB-projecten", icon: Briefcase, color: "text-cyan-600" },
  bmi_lead: { label: "BMI-leads", icon: Flame, color: "text-pink-600" },
  contract: { label: "Onderhoudscontracten", icon: ScrollText, color: "text-teal-600" },
  klacht: { label: "Klachten", icon: AlertTriangle, color: "text-rose-600" },
  relatie: { label: "Relaties (CRM)", icon: Building2, color: "text-sky-700" },
  employee: { label: "Medewerkers", icon: Users, color: "text-emerald-600" },
  mail: { label: "Mails", icon: FileText, color: "text-slate-600" },
};

// Snelle navigatie-links (altijd zichtbaar zonder query)
const QUICK_LINKS = [
  { titel: "Dashboard", url: "/dashboard", icon: Search },
  { titel: "Werkbonnen", url: "/werkbonnen", icon: Wrench },
  { titel: "Tickets", url: "/tickets", icon: Ticket },
  { titel: "Vaste klanten", url: "/vaste-klanten", icon: Building2 },
  { titel: "AI-Assistent", url: "/ai-assistent", icon: Search },
  { titel: "Facturen", url: "/facturen", icon: Receipt },
  { titel: "Cash flow", url: "/cash-flow", icon: Receipt },
  { titel: "Automations", url: "/automations", icon: Search },
];

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [melding, setMelding] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);  // stale-response guard
  const abortRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const RECENT_KEY = "bpt_recente_zoekopdrachten";
  useEffect(() => {
    if (open) {
      try { setRecent(JSON.parse(localStorage.getItem(RECENT_KEY) || "[]")); } catch { setRecent([]); }
    }
  }, [open]);
  const bewaarRecent = (q) => {
    if (!q || q.trim().length < 2) return;
    try {
      const lijst = [q.trim(), ...(JSON.parse(localStorage.getItem(RECENT_KEY) || "[]")).filter((x) => x !== q.trim())].slice(0, 6);
      localStorage.setItem(RECENT_KEY, JSON.stringify(lijst));
    } catch {}
  };

  // Global Cmd-K / Ctrl-K keyboard listener
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = (e) => {
      setOpen(true);
      const pre = e?.detail?.query;
      if (pre) setQuery(pre);
    };
    document.addEventListener("keydown", onKey);
    window.addEventListener("bpt:open-cmdk", onOpen);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("bpt:open-cmdk", onOpen);
    };
  }, []);

  // Debounced search met stale-response guard + abort van vorige call
  const runSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    // Bump sequence en abort vorige request
    const mySeq = ++requestSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/search`, {
        params: { q: q.trim(), limit: 6 },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });
      // Alleen resultaten toepassen als DIT nog de laatste request is
      if (mySeq === requestSeqRef.current) {
        setResults(res.data?.results || []);
        setMelding(res.data?.melding || null);
      }
    } catch (err) {
      // Aborted requests negeren
      if (mySeq === requestSeqRef.current && err?.name !== "CanceledError" && err?.name !== "AbortError") {
        setResults([]);
      }
    } finally {
      if (mySeq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const handleSelect = (url) => {
    bewaarRecent(query);
    setOpen(false);
    setQuery("");
    setResults([]);
    navigate(url);
  };

  // Groepeer results per module
  const grouped = results.reduce((acc, r) => {
    (acc[r.module] = acc[r.module] || []).push(r);
    return acc;
  }, {});

  if (!user) return null;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} commandProps={{ shouldFilter: false }} hideMobileChrome
      contentClassName="sm:max-w-2xl max-sm:!top-0 max-sm:!left-0 max-sm:!translate-x-0 max-sm:!translate-y-0 max-sm:!h-[100dvh] max-sm:!max-h-[100dvh] max-sm:!w-screen max-sm:!max-w-none max-sm:!rounded-none max-sm:!border-0 max-sm:flex max-sm:flex-col">
      <div className="flex items-center border-b pr-3 max-sm:py-1">
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder="Zoek klanten, werkbonnen, tickets, medewerkers…"
          className="flex-1 border-0 focus:ring-0 text-base sm:text-sm"
          autoFocus
          data-testid="global-search-input"
        />
        {loading && <Loader2 className="ml-2 h-4 w-4 animate-spin text-slate-400" />}
        <VoiceInput
          onTranscript={(t) => setQuery((t || "").replace(/[.?!]+\s*$/, "").trim())}
          append={false}
          language="nl"
          size="sm"
          className="ml-2 shrink-0"
          testId="global-search-voice"
        />
        <button onClick={() => setOpen(false)}
          className="sm:hidden ml-2 shrink-0 text-sm font-semibold text-blue-700 px-2 py-2"
          data-testid="global-search-annuleer">
          Annuleer
        </button>
      </div>
      <CommandList className="max-h-[420px] max-sm:max-h-none max-sm:flex-1 overscroll-contain">
        {!query && recent.length > 0 && (
          <CommandGroup heading="Recent gezocht">
            {recent.map((r) => (
              <CommandItem key={`recent-${r}`} onSelect={() => setQuery(r)} className="cursor-pointer"
                data-testid={`cmdk-recent-${r.replace(/\s+/g, "-")}`}>
                <History className="mr-2 h-4 w-4 text-slate-400" />
                <span>{r}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {!query && (
          <CommandGroup heading="Snelle navigatie">
            {QUICK_LINKS.map((l) => {
              const Icon = l.icon;
              return (
                <CommandItem
                  key={l.url}
                  onSelect={() => handleSelect(l.url)}
                  className="cursor-pointer"
                  data-testid={`cmdk-quick-${l.url.replace(/\//g, "-")}`}
                >
                  <Icon className="mr-2 h-4 w-4 text-slate-500" />
                  <span>{l.titel}</span>
                  <ArrowRight className="ml-auto h-3 w-3 opacity-40" />
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        {query && query.length < 2 && (
          <CommandEmpty>Typ minstens 2 letters om te zoeken…</CommandEmpty>
        )}
        {melding && <div className="px-3 py-1.5 text-xs text-amber-800 bg-amber-50 border-b border-amber-200" data-testid="search-melding">{melding}</div>}
        {query && query.length >= 2 && !loading && results.length === 0 && (
          <CommandEmpty>Geen resultaten voor "{query}"</CommandEmpty>
        )}
        {Object.entries(grouped).map(([mod, items], idx) => {
          const meta = MODULE_META[mod] || { label: mod, icon: Search, color: "text-slate-500" };
          const Icon = meta.icon;
          return (
            <React.Fragment key={mod}>
              {idx > 0 && <CommandSeparator />}
              <CommandGroup heading={meta.label}>
                {items.map((r) => (
                  <CommandItem
                    key={`${mod}-${r.id}`}
                    onSelect={() => handleSelect(r.url)}
                    className="cursor-pointer"
                    data-testid={`cmdk-result-${mod}-${r.id}`}
                  >
                    <Icon className={`mr-2 h-4 w-4 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.titel}</div>
                      {r.subtitel && (
                        <div className="text-xs text-slate-500 truncate">{r.subtitel}</div>
                      )}
                    </div>
                    <ArrowRight className="ml-2 h-3 w-3 opacity-40 flex-shrink-0" />
                  </CommandItem>
                ))}
              </CommandGroup>
            </React.Fragment>
          );
        })}
      </CommandList>
      <div className="border-t px-3 py-2 text-[11px] text-slate-500 hidden sm:flex items-center justify-between">
        <span>
          <kbd className="px-1.5 py-0.5 border border-slate-200 rounded bg-slate-50 mr-1">↑↓</kbd>
          navigeren
          <kbd className="px-1.5 py-0.5 border border-slate-200 rounded bg-slate-50 ml-2 mr-1">Enter</kbd>
          selecteren
          <kbd className="px-1.5 py-0.5 border border-slate-200 rounded bg-slate-50 ml-2 mr-1">Esc</kbd>
          sluiten
        </span>
        <span className="text-slate-400">BPT · Cmd-K search</span>
      </div>
    </CommandDialog>
  );
}
